import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useDetectSticky } from '../../../hooks/useDetectSticky';
import { STICKY_HEADER_BAR_DATA_ATTRIBUTE } from '../../HeaderBar/HeaderBar.constants';

export interface Link {
  text: string;
  anchor: string;
}

interface AnchorList {
  [anchor: string]: number;
}

interface ReturnAnchorList {
  anchors: AnchorList;
  currentAnchor: string;
  scrollTo(anchor: string): void;
}

const SCROLL_MARGIN_TOP = 20;
const ON_LOAD_HASH_INTERVAL = 230;
const ON_LOAD_HASH_MAXIMUM_WAITING_TIME = 2000;

const createAnchorList = (list: Link[]): AnchorList =>
  list.reduce((pv, cv: Link) => {
    pv[cv.anchor] = 0;
    return pv;
  }, {});

export const useAnchorList = (links: Link[], offsetElement: RefObject<HTMLElement>): ReturnAnchorList => {
  const flattedList = useMemo(() => createAnchorList(links), [links]);
  const [anchors, setAnchors] = useState<AnchorList>(flattedList);
  const { isSticky, getIsSticky } = useDetectSticky({ id: STICKY_HEADER_BAR_DATA_ATTRIBUTE });

  // Update ScrollMarginTop, so that hashurls have the correct offset
  useEffect(() => {
    let scrollMarginTop = SCROLL_MARGIN_TOP;

    if (isSticky) {
      scrollMarginTop += document
        .querySelector(`[${STICKY_HEADER_BAR_DATA_ATTRIBUTE}]`)
        .getBoundingClientRect()?.height;
    }

    document.getElementsByTagName('html').item(0).style.scrollMarginTop = `${scrollMarginTop}px`;
  }, [isSticky]);

  const currentAnchor = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const foundAnchor = Object.entries(anchors).filter(([_, value]) => value < 0);
    return foundAnchor.length > 0 ? foundAnchor[foundAnchor.length - 1][0] : null;
  }, [anchors]);

  const getAnchorTop = useCallback(
    (anchor: string) => {
      return (
        document.getElementById(anchor)?.getBoundingClientRect()?.top -
        offsetElement.current.getBoundingClientRect().height -
        (getIsSticky()
          ? document.querySelector(`[${STICKY_HEADER_BAR_DATA_ATTRIBUTE}]`).getBoundingClientRect()?.height
          : 0) -
        SCROLL_MARGIN_TOP
      );
    },
    [getIsSticky, offsetElement]
  );

  const updateAnchors = useCallback(() => {
    setAnchors((list) =>
      Object.entries(list).reduce((pv, [key]): AnchorList => {
        pv[key] = getAnchorTop(key) - 1 || 0;
        return pv;
      }, {})
    );
  }, [getAnchorTop]);

  const getScrollY = () => window.scrollY || window.pageYOffset;

  const scrollTo = useCallback(
    (anchor: string, behavior: ScrollBehavior = 'smooth') => {
      if (!anchor) {
        return;
      }

      const top = getAnchorTop(anchor) + getScrollY();
      window.scrollTo({
        top,
        behavior,
      });

      // eslint-disable-next-line @if6/fi-no-history
      history.pushState(null, null, `#${anchor}`);
    },
    [getAnchorTop]
  );

  // OnLoad: Reset the scroll position until the page is scrolled with all sticky elements knowing of their stickyness
  useEffect(() => {
    if (location.hash) {
      const anchor = location.hash.substring(1);
      const interval = setInterval(() => {
        const isAnchorReached = Math.abs(Math.trunc(getAnchorTop(anchor))) <= 0;
        if (isAnchorReached) {
          clearInterval(interval);
        }

        scrollTo(anchor, 'instant');
      }, ON_LOAD_HASH_INTERVAL);

      setTimeout(() => {
        clearInterval(interval);
      }, ON_LOAD_HASH_MAXIMUM_WAITING_TIME);

      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => updateAnchors();

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [updateAnchors]);

  return {
    anchors,
    currentAnchor,
    scrollTo,
  };
};
